export type ReleaseVersion = {
  readonly id: number;
  readonly version: string;
  readonly date: string | null;
  readonly descriptionItems: readonly string[];
};

type TrackerVersionDto = {
  readonly id: number;
  readonly name: string | null;
  readonly description: string | null;
  readonly released: boolean;
  readonly dueDate: string | null;
  readonly releasedAt: string | null;
};

export class InvalidReleaseResponseError extends Error {
  constructor() {
    super('The release response does not match the API contract.');
    this.name = 'InvalidReleaseResponseError';
  }
}

const isNullableString = (value: unknown): value is string | null | undefined =>
  value === null || value === undefined || typeof value === 'string';

const parseTrackerVersion = (value: unknown): TrackerVersionDto => {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('id' in value) ||
    typeof value.id !== 'number' ||
    !Number.isInteger(value.id) ||
    !('released' in value) ||
    typeof value.released !== 'boolean'
  ) {
    throw new InvalidReleaseResponseError();
  }

  const name = 'name' in value ? value.name : null;
  const description = 'description' in value ? value.description : null;
  const dueDate = 'dueDate' in value ? value.dueDate : null;
  const releasedAt = 'releasedAt' in value ? value.releasedAt : null;
  if (
    !isNullableString(name) ||
    !isNullableString(description) ||
    !isNullableString(dueDate) ||
    !isNullableString(releasedAt)
  ) {
    throw new InvalidReleaseResponseError();
  }

  return {
    id: value.id,
    name: name ?? null,
    description: description ?? null,
    dueDate: dueDate ?? null,
    releasedAt: releasedAt ?? null,
    released: value.released,
  };
};

export const splitReleaseDescription = (
  description: string | null,
): readonly string[] => {
  if (!description?.trim()) return [];

  return description.split(/\r?\n/).flatMap((line) => {
    const text = line.replace(/^\s*(?:[-*•]\s+|\d+[.)]\s+)/, '').trim();
    const sentences = text.match(/.*?(?:[.!?…]+["»”')\]]*(?=\s|$)|$)/gu);
    return (sentences ?? []).map((sentence) => sentence.trim()).filter(Boolean);
  });
};

const validDate = (value: string | null): string | null =>
  value && Number.isFinite(Date.parse(value)) ? value : null;

export const formatReleaseDate = (value: string | null): string => {
  const parts = value?.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
  return parts ? `${parts[3]}.${parts[2]}.${parts[1]}` : '—';
};

export const normalizeReleaseVersions = (
  response: unknown,
): readonly ReleaseVersion[] => {
  if (!Array.isArray(response)) throw new InvalidReleaseResponseError();

  const versions: ReleaseVersion[] = [];
  for (const entry of response) {
    const version = parseTrackerVersion(entry);
    if (!version.released || !version.name?.trim()) continue;

    versions.push({
      id: version.id,
      version: version.name.trim(),
      date: validDate(version.dueDate) ?? validDate(version.releasedAt),
      descriptionItems: splitReleaseDescription(version.description),
    });
  }

  return versions.sort(
    (left, right) =>
      (right.date ? Date.parse(right.date) : -Infinity) -
      (left.date ? Date.parse(left.date) : -Infinity),
  );
};
